import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, Boxes, Link2 } from 'lucide-react';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit } from '../lib/embedChannel';

/**
 * ER Diagram embed.
 *
 * Mounted at `/embed/v2/er-diagram?token=<jwt>`.
 *
 * Simple, dependency-light layout that renders entities as boxes on a
 * two-column grid and draws SVG lines between joined pairs. Good
 * enough for teams to visualise their model without dragging in a
 * heavier graph library — and refresh-safe because the layout is
 * derived deterministically from the node order returned by the
 * backend.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authenticate(token) {
  const res = await fetch(`${API}/api/embed/v2/authenticate`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('authenticate failed');
  return res.json();
}

async function loadGraph(token) {
  const res = await fetch(`${API}/api/embed/v2/er-diagram`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail?.message || `er-diagram ${res.status}`);
  }
  return res.json();
}

const NODE_W = 220;
const NODE_H_BASE = 44;
const NODE_H_ATTR = 18;
const COL_GAP = 72;
const ROW_GAP = 32;

function layoutNodes(nodes) {
  // Two-column grid: alternate to reduce cross-line noise.
  const positioned = [];
  let leftY = 24;
  let rightY = 24;
  nodes.forEach((n, i) => {
    const h = NODE_H_BASE + (n.attributes?.length || 0) * NODE_H_ATTR;
    const isLeft = i % 2 === 0;
    positioned.push({
      ...n,
      x: isLeft ? 24 : 24 + NODE_W + COL_GAP,
      y: isLeft ? leftY : rightY,
      w: NODE_W,
      h,
    });
    if (isLeft) leftY += h + ROW_GAP;
    else rightY += h + ROW_GAP;
  });
  const totalW = 24 + NODE_W * 2 + COL_GAP + 24;
  const totalH = Math.max(leftY, rightY) + 24;
  return { positioned, totalW, totalH };
}

function anchorFor(node, side) {
  const cx = side === 'right' ? node.x + node.w : node.x;
  const cy = node.y + node.h / 2;
  return { cx, cy };
}

export default function EmbedErDiagramPage() {
  const token = useEmbedToken();
  const [claims, setClaims] = useState(null);
  const [branding, setBranding] = useState(null);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const c = await authenticate(token);
        if (cancelled) return;
        setClaims(c);
        setBranding(c?.branding || null);
        emit('viewer.identified', { sub: c?.sub, roles: c?.roles || [], capabilities: c?.capabilities || [] });
        const g = await loadGraph(token);
        if (!cancelled) {
          setGraph(g);
          emit('er_diagram.loaded', { nodes: g.counts?.nodes, edges: g.counts?.edges });
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const layout = useMemo(() => {
    if (!graph?.nodes?.length) return null;
    const { positioned, totalW, totalH } = layoutNodes(graph.nodes);
    const byId = new Map(positioned.map((n) => [n.id, n]));
    const edges = (graph.edges || []).map((e) => {
      const from = byId.get(e.from);
      const to = byId.get(e.to);
      if (!from || !to) return null;
      const fromSide = from.x < to.x ? 'right' : 'left';
      const toSide = fromSide === 'right' ? 'left' : 'right';
      return {
        ...e,
        p1: anchorFor(from, fromSide),
        p2: anchorFor(to, toSide),
      };
    }).filter(Boolean);
    return { positioned, totalW, totalH, edges };
  }, [graph]);

  const productName = brandingProductName(branding);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="embed-er-missing-token">
        Waiting for session token…
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col bg-background text-foreground"
      style={brandingRootStyle(branding)}
      data-testid="embed-er-root"
    >
      <header className="px-4 py-3 border-b border-border/40 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Boxes className="w-4 h-4 text-indigo-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="embed-er-product-name">
              {productName || 'ER Diagram'}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              Semantic model relationships
            </div>
          </div>
        </div>
        {claims && (
          <div className="text-[10px] text-muted-foreground text-right shrink-0" data-testid="embed-er-viewer">
            <div className="font-mono">{claims.sub}</div>
            <div>{(claims.capabilities || ['read']).join(', ')}</div>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-auto p-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="embed-er-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading model graph…
          </div>
        )}
        {error && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200 flex items-start gap-2" data-testid="embed-er-error">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}
        {!loading && !error && graph?.nodes?.length === 0 && (
          <div className="text-center text-[12px] text-muted-foreground italic mt-8" data-testid="embed-er-empty">
            No semantic entities defined for this workspace yet.
          </div>
        )}
        {layout && (
          <div
            className="relative"
            style={{ width: layout.totalW, height: layout.totalH }}
            data-testid="embed-er-canvas"
          >
            <svg
              className="absolute inset-0 pointer-events-none"
              width={layout.totalW}
              height={layout.totalH}
            >
              {layout.edges.map((e, i) => {
                const midX = (e.p1.cx + e.p2.cx) / 2;
                const d = `M ${e.p1.cx} ${e.p1.cy} C ${midX} ${e.p1.cy}, ${midX} ${e.p2.cy}, ${e.p2.cx} ${e.p2.cy}`;
                return (
                  <g key={i} data-testid={`embed-er-edge-${e.from}-${e.to}`}>
                    <path d={d} stroke="#818cf8" strokeWidth="1.5" fill="none" opacity="0.6" />
                    <circle cx={e.p1.cx} cy={e.p1.cy} r="3" fill="#818cf8" />
                    <circle cx={e.p2.cx} cy={e.p2.cy} r="3" fill="#818cf8" />
                  </g>
                );
              })}
            </svg>
            {layout.positioned.map((n) => (
              <div
                key={n.id}
                className="absolute rounded-md border border-indigo-500/40 bg-background shadow-lg overflow-hidden"
                style={{ left: n.x, top: n.y, width: n.w }}
                data-testid={`embed-er-node-${n.id}`}
              >
                <div className="px-2.5 py-1.5 border-b border-indigo-500/30 bg-indigo-500/[0.08] flex items-center gap-1.5">
                  <Link2 className="w-3 h-3 text-indigo-300" />
                  <div className="text-[11px] font-semibold truncate">{n.label}</div>
                </div>
                <div>
                  {(n.attributes || []).map((a) => (
                    <div
                      key={a.name}
                      className="px-2.5 py-1 text-[10px] font-mono flex items-center justify-between border-b border-border/20 last:border-0"
                      data-testid={`embed-er-attr-${n.id}-${a.name}`}
                    >
                      <span className="truncate text-foreground">{a.display_name || a.name}</span>
                      <span className={`ml-2 shrink-0 text-[9px] uppercase tracking-wider ${
                        a.semantic_type === 'measure'
                          ? 'text-emerald-300'
                          : a.semantic_type === 'dimension'
                            ? 'text-amber-300'
                            : 'text-muted-foreground'
                      }`}>{a.data_type}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {graph && (
        <footer className="px-4 py-2 border-t border-border/40 text-[10px] text-muted-foreground font-mono flex items-center justify-between" data-testid="embed-er-counts">
          <span>{graph.counts?.nodes || 0} entities</span>
          <span>{graph.counts?.edges || 0} joins</span>
        </footer>
      )}
    </div>
  );
}
